import Vue from 'vue';
import { NOTIFICATIONS } from '../utils/constants';
import { storeGet } from '@/store/store-utils';

const getDefaultState = () => ({
  notifications: [],
  availableFeatures: [],
  isLoadingNotifications: false,
  isLoadingFeatures: false,
  isClientNotEnabled: false,
  isSavingNotification: false,
  isUpdatingStatus: false,
});

export default {
  namespaced: true,
  state: getDefaultState(),
  getters: {
    notifications: (state) => state.notifications,
    availableFeatures: (state) => state.availableFeatures,
    isLoadingNotifications: (state) => state.isLoadingNotifications,
    isLoadingFeatures: (state) => state.isLoadingFeatures,
    isClientNotEnabled: (state) => state.isClientNotEnabled,
    isSavingNotification: (state) => state.isSavingNotification,
    isUpdatingStatus: (state) => state.isUpdatingStatus,
    
    schedulersData: (state) => {
      return state.notifications
        .filter((item) => item.notificationType === NOTIFICATIONS.NOTIFICATION_TYPE.SCHEDULERS)
        .map((item, index) => ({
          __keys: index,
          indexno: index,
          _id: item._id,
          feature: item.feature,
          status: item.status,
          enabledordisabled: item.status,
          statusvalue: item.status ? NOTIFICATIONS.STATUS.ENABLED : NOTIFICATIONS.STATUS.DISABLED,
          scheduler: "24",
          labelName: item.labelName ?? "",
          periodCount: item?.periodCount ?? 1,
          notificationType: item?.notificationType ?? NOTIFICATIONS.NOTIFICATION_TYPE.SCHEDULERS,
        }));
    },
    
    realTimeData: (state) => {
      return state.notifications
        .filter((item) => item.notificationType === NOTIFICATIONS.NOTIFICATION_TYPE.REALTIME)
        .map((item, index) => ({
          __keys: index,
          _id: item._id,
          indexno: index,
          feature: item.feature,
          status: item.status,
          enabledordisabled: item.status,
          labelName: item.labelName ?? "",
          statusvalue: item.status ? NOTIFICATIONS.STATUS.ENABLED : NOTIFICATIONS.STATUS.DISABLED,
          notificationType: item?.notificationType ?? NOTIFICATIONS.NOTIFICATION_TYPE.REALTIME,
        }));
    }
  },
  mutations: {
    SET_NOTIFICATIONS(state, payload) {
      state.notifications = payload;
    },
    SET_AVAILABLE_FEATURES(state, payload) {
      state.availableFeatures = payload;
    },
    SET_LOADING_NOTIFICATIONS(state, payload) {
      state.isLoadingNotifications = payload;
    },
    SET_LOADING_FEATURES(state, payload) {
      state.isLoadingFeatures = payload;
    },
    SET_CLIENT_NOT_ENABLED(state, payload) {
      state.isClientNotEnabled = payload;
    },
    SET_SAVING_NOTIFICATION(state, payload) {
      state.isSavingNotification = payload;
    },
    SET_UPDATING_STATUS(state, payload) {
      state.isUpdatingStatus = payload;
    },
    RESET_STATE(state) {
      Object.assign(state, getDefaultState());
    }
  },
  actions: {
 async createRealTimeNotifications({ commit}) {
      try {
        commit('SET_LOADING_NOTIFICATIONS', true);
         const response = await Vue.axios.post(
          NOTIFICATIONS.API_ENDPOINTS.CREATE_REAL_TIME_NOTIFICATIONS
        );
        if (response?.data?.status !== "success") {
          throw new Error(response?.data?.message || NOTIFICATIONS.MESSAGES.ERROR_CREATE);
        }
      } catch (err) {
        console.error('[NotificationStore]: createRealTimeNotifications error', err);
        commit("sbError", err?.response?.data?.message || err?.message || NOTIFICATIONS.MESSAGES.ERROR_CREATE, { root: true });
      } finally {
        commit('SET_LOADING_NOTIFICATIONS', false);
      }
    },

    async fetchNotifications({ commit, rootState }) {
      try {
        commit('SET_LOADING_NOTIFICATIONS', true);
        const { data } = await storeGet(
          Vue.axios, rootState,
          NOTIFICATIONS.API_ENDPOINTS.GET_ALL_FEATURES_BY_CLIENT,
          { type: NOTIFICATIONS.TYPE }
        );

        if (data?.status !== "success" || !data?.data) {
            if (data?.message === "Scheduler not enabled.") {
                commit('SET_CLIENT_NOT_ENABLED', true);
                commit('SET_NOTIFICATIONS', []);
                return;
            }
            throw new Error(data?.message ?? NOTIFICATIONS.MESSAGES.ERROR_GET);
        }

        commit('SET_NOTIFICATIONS', data.data);
        commit('SET_CLIENT_NOT_ENABLED', false);

      } catch (err) {
        commit("sbError", err.message ?? "Unknown error!", { root: true });
      } finally {
        commit('SET_LOADING_NOTIFICATIONS', false);
      }
    },

    async fetchAvailableFeatures({ commit, rootState }) {
      try {
        commit('SET_LOADING_FEATURES', true);
        const { data } = await storeGet(
          Vue.axios, rootState,
          NOTIFICATIONS.API_ENDPOINTS.GET_ALL_AVAILABLE_FEATURES,
          { type: NOTIFICATIONS.TYPE }
        );
        if (data?.status !== "success" || !data?.data) {
          throw new Error(data?.message || NOTIFICATIONS.MESSAGES.ERROR_GET);
        }
        commit('SET_AVAILABLE_FEATURES', data.data || []);
      } catch (err) {
        commit("sbError", err.message || NOTIFICATIONS.MESSAGES.ERROR_GET, { root: true });
      } finally {
        commit('SET_LOADING_FEATURES', false);
      }
    },

    async saveNotification({ commit, dispatch }, { data, isUpdate = false, isStatusUpdate = false }) {
        const loadingMutation = isStatusUpdate ? 'SET_UPDATING_STATUS' : 'SET_SAVING_NOTIFICATION';
        commit(loadingMutation, true);
        
        try {
            const response = await Vue.axios.post(
                NOTIFICATIONS.API_ENDPOINTS.CREATE_OR_UPDATE_NOTIFICATION,
                { featureDetails: data }
            );

            if (response?.data?.status !== "success" || !response?.data?.data) {
                throw new Error(response?.data?.message || (isUpdate ? NOTIFICATIONS.MESSAGES.ERROR_UPDATE : NOTIFICATIONS.MESSAGES.ERROR_ADD));
            }

            if (isStatusUpdate) {
                const message = data.flag 
                    ? NOTIFICATIONS.MESSAGES.SUCCESS_ENABLED 
                    : NOTIFICATIONS.MESSAGES.SUCCESS_DISABLED;
                commit("sbSuccess", message, { root: true });
            } else if (isUpdate) {
                commit("sbSuccess", NOTIFICATIONS.MESSAGES.SUCCESS_UPDATE, { root: true });
            } else {
                commit("sbSuccess", NOTIFICATIONS.MESSAGES.SUCCESS_ADD, { root: true });
            }
            
            await dispatch('fetchNotifications');
            // await dispatch('fetchAvailableFeatures'); // Usually not needed unless features change status

        } catch (err) {
            commit("sbError", err?.message || (isUpdate ? NOTIFICATIONS.MESSAGES.ERROR_UPDATE : NOTIFICATIONS.MESSAGES.ERROR_ADD), { root: true });
            throw err;
        } finally {
            commit(loadingMutation, false);
        }
    }
  }
};
